import React, { useEffect, useState } from 'react';
import i18n from '../../../../../i18n';
import BzFormControlWithHook from '../../../../../components/forms/BzFormControlWithHook';
import { useFormInput, ValidatorType } from '../../../../../hooks/useFormInput';
import CheckBox from '../../../../../components/ui/checkBox/CheckBox';
import _ from 'lodash';
import { Col, Row } from 'react-bootstrap';
import { useIsMount } from '../../../../../hooks/useIsMountHook';
import InlineHelp from '../../../../../components/ui/inlineHelp/InlineHelpProps';
import Button from 'react-bootstrap/Button';
import BzmIcon from '../../../../../components/icons/BzmIcon';
import { REGEX_VALID_HTTP_URL } from '../../../../../utils/regexUtils';
import SslConfiguration, {
  AssetAssignmentBundle,
  SSlConfigMode,
} from '../../../../MockServices/Tabs/MockServices/SslConfiguration';

interface RedirectToLiveProps {
  redirectUrl?: string;
  redirectUrlChanged: (urlValue: string | undefined) => void;
  assetBundle?: AssetAssignmentBundle;
  assetsChanged: (assetAssignment: AssetAssignmentBundle) => void;
  setValidity?: (isValid: boolean) => void;
  failoverEnabled: boolean;
  onFailoverChanged: (failoverEnabled: boolean) => void;
}

const RedirectToLive = (props: RedirectToLiveProps) => {
  const [redirectUrl, setRedirectUrl] = useState<null | string>(props.redirectUrl ? props.redirectUrl : undefined);
  const [isDisabled, setIsDisabled] = useState(_.isNil(props.redirectUrl));
  const isMount = useIsMount();

  const liveSystemInputHook = useFormInput(
    redirectUrl,
    fn => {
      if (fn.isValid) {
        setRedirectUrl(fn.value as string);
        updateRedirectUrlDebounced(fn.value as string);
      }
    },
    ...[
      {
        type: ValidatorType.REQUIRED,
        invalidMessage: i18n.t('mockServices:errorMsgs.errorLiveSystemEndpointEmpty'),
      },
      {
        type: ValidatorType.INVALID_CHARACTERS,
        value: REGEX_VALID_HTTP_URL,
        invalidMessage: i18n.t('mockServices:errorMsgs.errorLiveSystemEndpoint'),
      },
    ],
  );

  const updateRedirectUrlDebounced = _.debounce((redirectUrl: string) => {
    props.redirectUrlChanged(redirectUrl);
  }, 400);

  useEffect(() => {
    if (isDisabled === true) {
      if (liveSystemInputHook.value !== undefined) {
        liveSystemInputHook.resetValue(undefined);
        props.redirectUrlChanged(undefined);
      }
    } else {
      if (!isMount) {
        liveSystemInputHook.resetValue('http://');
      }
    }
  }, [isDisabled]);

  return (
    <div className={'pl-1 pr-1'}>
      <Row>
        <Col md={4}>
          {isDisabled && (
            <>
              <p>Redirect to Live System is not defined.</p>
              <Button
                onClick={() => {
                  setIsDisabled(false);
                }}
                className="btn btn-primary btn-circular"
                title={'Add '}
              >
                <BzmIcon variant={'icon-bzm-add'} />
              </Button>
            </>
          )}

          {!isDisabled && (
            <div>
              <div className="card">
                <div className="card-body">
                  {/* Live System input */}
                  <label htmlFor={'redirectToLive'}>{i18n.t('transactions:redirectToLive.label')}</label>
                  <BzFormControlWithHook
                    autoComplete={'off'}
                    id={'redirectToLive'}
                    useFormInputHook={liveSystemInputHook}
                    placeholder={i18n.t('transactions:redirectToLive.placeholder')}
                    required={false}
                    hideErrors={false}
                  />

                  {/* Fail Over */}
                  <div className="mt-4 pl-1">
                    <CheckBox
                      id={'failOverCheckbox'}
                      label={i18n.t('transactions:redirectToLive.failOver.title')}
                      checked={props.failoverEnabled}
                      onCheck={() => {
                        props.onFailoverChanged(!props.failoverEnabled);
                      }}
                      selectAll={false}
                      disabled={isDisabled}
                    />
                    <InlineHelp className={'ml-5'}>{i18n.t('transactions:redirectToLive.failOver.desc')}</InlineHelp>

                    <SslConfiguration
                      heading={<label className={'mt-5'}>SSL Authentication</label>}
                      assetsBundle={props.assetBundle}
                      onAssignmentChanged={props.assetsChanged}
                      disabled={false}
                      configMode={SSlConfigMode.CLIENT}
                    />
                  </div>

                  {/* Footer */}
                  <div className={'text-right mt-4'}>
                    <Button
                      onClick={() => {
                        props.assetsChanged({ assets: [] });
                        setIsDisabled(true);
                      }}
                      className="btn btn-danger btn-circular"
                      title={'Remove Live System Endpoint '}
                    >
                      <BzmIcon variant={'icon-bzm-delete'} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default RedirectToLive;
